import { useEffect, useMemo, useState } from "react";
import { medicalSpecialtyOptions } from "../utils/Constants";
import InputDropdown from "./InputDropdown";

/** Regulation number must belong to the selected specialty before we push filter (avoids a stale pair when specialty changes first). */
const regNumberMatchesSpecialty = (specialty, reg) => {
  if (!specialty?.regCodes?.length || !reg?.value) return false;
  return specialty.regCodes.some((code) => code.substring(0, 8) === reg.value);
};

const GraphFilters = ({
  summary,
  setFilter,
  productCodes,
  applicants,
  selectedApplicant,
  selectedProductCode,
  handleFilterByProdCode,
  handleFilterByApplicant,
}) => {
  const [selectedMedicalSpecialty, setSelectedMedicalSpecialty] = useState({
    label: " ",
    value: "",
  });
  const [selectedRegNumber, setSelectedRegNumber] = useState({
    label: " ",
    value: "",
  });
  const [regNumberOptions, setRegNumberOptions] = useState("");
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const orderedMedicalSpecialtyOptions = useMemo(() => {
    return medicalSpecialtyOptions.sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
  }, []);

  useEffect(() => {
    setSelectedMedicalSpecialty(orderedMedicalSpecialtyOptions[0]);
  }, [orderedMedicalSpecialtyOptions]);

  useEffect(() => {
    if (selectedMedicalSpecialty && selectedMedicalSpecialty.regCodes) {
      const orderedRegCodes = selectedMedicalSpecialty.regCodes.map((item) => {
        return { label: item, value: item.substring(0, 8) };
      });
      setRegNumberOptions(orderedRegCodes);
      setSelectedRegNumber(orderedRegCodes[0]);
    }
  }, [selectedMedicalSpecialty]);

  useEffect(() => {
    if (
      !hasUserInteracted || // 🚫 block initial auto-trigger
      !selectedMedicalSpecialty?.value ||
      !selectedRegNumber?.value
    ) {
      return;
    }
    // Without this check, changing specialty updates medicalSpecialty in one commit while
    // selectedRegNumber still reflects the *previous* specialty until the next effect runs,
    // so setFilter fires twice and generate_graph_data is called twice.
    if (!regNumberMatchesSpecialty(selectedMedicalSpecialty, selectedRegNumber)) {
      return;
    }

    setFilter({
      medicalSpecialty: selectedMedicalSpecialty.value,
      productCode: selectedRegNumber.value,
    });
  }, [selectedRegNumber, selectedMedicalSpecialty, hasUserInteracted]);

  const handleMedicalSpecialtySelect = (option) => {
    setHasUserInteracted(true);
    setSelectedMedicalSpecialty(option);
  };

  const handleRegNumberOptionsSelect = (option) => {
    setHasUserInteracted(true);
    setSelectedRegNumber(option);
  };

  return (
    <div className="glass fixed z-30 mt-1 ml-1 w-80 rounded p-4 shadow-none">
      <div className="form-control mb-4">
        <label className="label">
          <span className="status status-lg bg-green-700"></span>
          <span className="label-text">Medical Specialty</span>
        </label>
        <InputDropdown
          options={orderedMedicalSpecialtyOptions}
          onSelect={handleMedicalSpecialtySelect}
          selected={selectedMedicalSpecialty.label}
          placeholder="Medical Specialty"
        />
      </div>
      <div className="form-control mb-4">
        <label className="label">
          <span className="status status-lg bg-red-700"></span>
          <span className="label-text">Regulation Number</span>
        </label>
        <InputDropdown
          options={regNumberOptions}
          onSelect={handleRegNumberOptionsSelect}
          selected={selectedRegNumber.label}
          placeholder="Regulation Number"
        />
      </div>
      <div className="form-control mb-4">
        <label className="label">
          <span className="status status-lg bg-blue-700"></span>
          <span className="label-text">Product Code</span>
        </label>
        <InputDropdown
          options={productCodes}
          onSelect={handleFilterByProdCode}
          selected={selectedProductCode.label}
          placeholder="Product Code"
        />
      </div>
      <div className="form-control mb-4">
        <label className="label">
          <span className="status status-lg bg-gray-700"></span>
          <span className="label-text">Applicant</span>
        </label>
        <InputDropdown
          options={applicants}
          onSelect={handleFilterByApplicant}
          selected={selectedApplicant.label}
          placeholder="Applicant"
        />
      </div>
      <h3 className="h-[calc(100vh-540px)] overflow-scroll text-sm">
        {summary}
      </h3>
    </div>
  );
};
export default GraphFilters;
